import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import app from "../../../firebaseConfig";

ChartJS.register(ArcElement, Tooltip, Legend);

function GraficaCircularVentas() {
  const [ventasPorCategoria, setVentasPorCategoria] = useState({});
  const [totalPedidos, setTotalPedidos] = useState(0);
  const [totalProductosVendidos, setTotalProductosVendidos] = useState(0);
  const [totalDineroRecaudado, setTotalDineroRecaudado] = useState(0);
  const [costoTotal, setCostoTotal] = useState(0);
  const [ganancia, setGanancia] = useState(0);
  // ⚠️ Cambio: por defecto "hoy"
  const [filtroTiempo, setFiltroTiempo] = useState("hoy");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  // Estados animados
  const [totalPedidosAnimado, setTotalPedidosAnimado] = useState(0);
  const [totalProductosAnimado, setTotalProductosAnimado] = useState(0);
  const [totalDineroAnimado, setTotalDineroAnimado] = useState(0);
  const [costoTotalAnimado, setCostoTotalAnimado] = useState(0);
  const [gananciaAnimada, setGananciaAnimada] = useState(0);

  // ---- Util: parsear/extraer fecha de un pedido (maneja number/ISO/string) ----
  function parsearFecha(fechaRaw) {
    if (!fechaRaw && fechaRaw !== 0) return null;
    if (fechaRaw instanceof Date) return isNaN(fechaRaw) ? null : fechaRaw;
    if (typeof fechaRaw === "number") {
      const d = new Date(fechaRaw);
      return isNaN(d) ? null : d;
    }
    // algunos pedidos pueden guardar timestamps dentro de objetos (ej: { seconds: ..., nanoseconds: ... })
    if (typeof fechaRaw === "object" && fechaRaw !== null) {
      // compatibilidad con Firebase Timestamp-like
      if (fechaRaw.seconds) {
        return new Date(fechaRaw.seconds * 1000);
      }
      return null;
    }
    // string
    const d = new Date(fechaRaw);
    return isNaN(d) ? null : d;
  }

  // ---- Filtro por fechas (ahora acepta Date/number/string y es más tolerante) ----
  function cumpleFiltro(fechaPedidoRaw) {
    // si filtro "todo" no filtramos
    if (filtroTiempo === "todo") return true;
    const fechaPedidoObj = parsearFecha(fechaPedidoRaw);
    if (!fechaPedidoObj) return false;

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const hoyEnd = new Date();
    hoyEnd.setHours(23, 59, 59, 999);

    const ayer = new Date(hoy);
    ayer.setDate(hoy.getDate() - 1);
    const ayerEnd = new Date(ayer);
    ayerEnd.setHours(23, 59, 59, 999);

    const haceUnaSemana = new Date(hoy);
    haceUnaSemana.setDate(hoy.getDate() - 7);
    const haceSeisMeses = new Date(hoy);
    haceSeisMeses.setMonth(hoy.getMonth() - 6);
    const haceUnMes = new Date(hoy);
    haceUnMes.setMonth(hoy.getMonth() - 1);
    const haceUnAño = new Date(hoy);
    haceUnAño.setFullYear(hoy.getFullYear() - 1);

    // Normalizamos el rango [start, end] para comparaciones inclusivas
    if (filtroTiempo === "hoy") {
      // comparar día completo
      return fechaPedidoObj >= hoy && fechaPedidoObj <= hoyEnd;
    }
    if (filtroTiempo === "ayer") return fechaPedidoObj >= ayer && fechaPedidoObj <= ayerEnd;
    if (filtroTiempo === "semana") return fechaPedidoObj >= haceUnaSemana && fechaPedidoObj <= hoyEnd;
    if (filtroTiempo === "mes") return fechaPedidoObj >= haceUnMes && fechaPedidoObj <= hoyEnd;
    if (filtroTiempo === "6meses") return fechaPedidoObj >= haceSeisMeses && fechaPedidoObj <= hoyEnd;
    if (filtroTiempo === "año") return fechaPedidoObj >= haceUnAño && fechaPedidoObj <= hoyEnd;

    if (filtroTiempo === "rango" && fechaInicio && fechaFin) {
      const inicio = new Date(fechaInicio);
      inicio.setHours(0, 0, 0, 0);
      const fin = new Date(fechaFin);
      fin.setHours(23, 59, 59, 999); // inclusivo hasta el final del día
      return fechaPedidoObj >= inicio && fechaPedidoObj <= fin;
    }

    return false;
  }

  // ---- Contar órdenes que cumplan filtro dentro de un nodo ----
  function countFiltered(ordersObj) {
    if (!ordersObj) return 0;
    return Object.values(ordersObj).reduce((acc, pedido) => {
      // intentar extraer varias claves comunes si existieran
      const posibleFecha =
        pedido?.fecha ?? pedido?.createdAt ?? pedido?.timestamp ?? pedido?.date ?? null;
      return cumpleFiltro(posibleFecha) ? acc + 1 : acc;
    }, 0);
  }

  // ---- Cargar datos desde Firebase ----
  useEffect(() => {
    const db = getDatabase(app);
    const ordenesRef = ref(db, "nature/Awoo/Ordenes");
    const ordenesPagadasRef = ref(db, "nature/Awoo/OrdenesPagadas");

    // procesar datos: ahora totalPedidos respeta el filtro
    const procesarDatos = (ordenes, ordenesPagadas) => {
      let totalPedidosTemp = 0;
      let totalProductosTemp = 0;
      let totalDineroTemp = 0;
      let costoTotalTemp = 0;
      let gananciaTotalTemp = 0;
      let ventasPorCategoriaTemp = {};

      // ⚠️ Cambio: contar solo órdenes que cumplan el filtro en ambos nodos
      totalPedidosTemp = countFiltered(ordenes) + countFiltered(ordenesPagadas);

      // Procesamos las ordenes pagadas para métricas económicas (solo si cumplen filtro)
      Object.values(ordenesPagadas || {}).forEach((pedido) => {
        const fechaPedido = pedido?.fecha ?? pedido?.createdAt ?? pedido?.timestamp ?? null;
        if (!cumpleFiltro(fechaPedido)) return;

        const cantidad = Number(pedido.cantidad) || 0;
        const categoria = pedido.categoria || "Sin categoría";
        const precioTotal = Number(pedido.precioTotal) || 0;
        const costo = Number(pedido.costoTotal) || 0;
        const ganancia = Number(pedido.gananciaTotal) || 0;

        totalProductosTemp += cantidad;
        totalDineroTemp += precioTotal;
        costoTotalTemp += costo;
        gananciaTotalTemp += ganancia;

        if (!ventasPorCategoriaTemp[categoria]) ventasPorCategoriaTemp[categoria] = 0;
        ventasPorCategoriaTemp[categoria] += cantidad;
      });

      // Actualizar estados
      setVentasPorCategoria(ventasPorCategoriaTemp);
      setTotalPedidos(totalPedidosTemp);
      setTotalProductosVendidos(totalProductosTemp);
      setTotalDineroRecaudado(totalDineroTemp);
      setCostoTotal(costoTotalTemp);
      setGanancia(gananciaTotalTemp);
    };

    // Suscripciones independientes y llamadas seguras a procesarDatos cuando cualquiera cambie
    let ordenesData = {};
    let ordenesPagadasData = {};

    const unsub1 = onValue(ordenesRef, (snap1) => {
      ordenesData = snap1.val() || {};
      procesarDatos(ordenesData, ordenesPagadasData);
    });

    const unsub2 = onValue(ordenesPagadasRef, (snap2) => {
      ordenesPagadasData = snap2.val() || {};
      procesarDatos(ordenesData, ordenesPagadasData);
    });

    return () => {
      // limpiar ambas suscripciones
      try {
        unsub1();
      } catch (e) {}
      try {
        unsub2();
      } catch (e) {}
    };
  }, [filtroTiempo, fechaInicio, fechaFin]);

  // ---- Animaciones de valores ----
  useEffect(() => {
    const animateValue = (setter, finalValue) => {
      let start = 0;
      const duration = 1000;
      const step = Math.max(Math.floor(finalValue / 100), 1);
      const increment = () => {
        start += step;
        if (start >= finalValue) setter(finalValue);
        else {
          setter(start);
          requestAnimationFrame(increment);
        }
      };
      increment();
    };

    animateValue(setTotalPedidosAnimado, totalPedidos);
    animateValue(setTotalProductosAnimado, totalProductosVendidos);
    animateValue(setTotalDineroAnimado, totalDineroRecaudado);
    animateValue(setCostoTotalAnimado, costoTotal);
    animateValue(setGananciaAnimada, ganancia);
  }, [totalPedidos, totalProductosVendidos, totalDineroRecaudado, costoTotal, ganancia]);

  // ---- Render ----
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px" }}>
      {/* Selector de tiempo */}
      <div style={{ marginBottom: "20px" }}>
        <select value={filtroTiempo} onChange={(e) => setFiltroTiempo(e.target.value)}>
          <option value="todo">Todo</option>
          <option value="hoy">Hoy</option>
          <option value="ayer">Ayer</option>
          <option value="semana">1 Semana</option>
          <option value="mes">1 Mes</option>
          <option value="6meses">6 Meses</option>
          <option value="año">1 Año</option>
          <option value="rango">Rango Manual</option>
        </select>

        {filtroTiempo === "rango" && (
          <>
            <input type="date" onChange={(e) => setFechaInicio(e.target.value)} />
            <input type="date" onChange={(e) => setFechaFin(e.target.value)} />
          </>
        )}
      </div>

      <div style={{ display: "flex", gap: "40px" }}>
        <div style={{ width: "250px", height: "250px" }}>
          <Doughnut
            data={{
              labels: Object.keys(ventasPorCategoria),
              datasets: [
                {
                  data: Object.values(ventasPorCategoria),
                  backgroundColor: [
                    "#FF4500",
                    "#1E90FF",
                    "#32CD32",
                    "#FFD700",
                    "#8A2BE2",
                    "#FF69B4",
                    "#00CED1",
                  ],
                },
              ],
            }}
          />
        </div>

        {/* Totales */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", color: "#fff" }}>
          <div style={{ fontSize: "20px", fontWeight: "bold" }}>📦 Total de Pedidos</div>
          <div style={{ fontSize: "30px", fontWeight: "bold", color: "#FFD700" }}>{totalPedidosAnimado}</div>

          <div style={{ fontSize: "20px", fontWeight: "bold", marginTop: "10px" }}>📦 Productos Vendidos</div>
          <div style={{ fontSize: "30px", fontWeight: "bold", color: "#36A2EB" }}>{totalProductosAnimado}</div>

          <div style={{ fontSize: "20px", fontWeight: "bold", marginTop: "10px" }}>💰 Total Recaudado</div>
          <div style={{ fontSize: "30px", fontWeight: "bold", color: "#4CAF50" }}>
            ${totalDineroAnimado.toLocaleString()}
          </div>

          <div style={{ fontSize: "20px", fontWeight: "bold", marginTop: "10px" }}>💸 Costo Total</div>
          <div style={{ fontSize: "30px", fontWeight: "bold", color: "#E74C3C" }}>
            ${costoTotalAnimado.toLocaleString()}
          </div>

          <div style={{ fontSize: "20px", fontWeight: "bold", marginTop: "10px" }}>📈 Ganancia</div>
          <div style={{ fontSize: "30px", fontWeight: "bold", color: "#2ECC71" }}>
            ${gananciaAnimada.toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GraficaCircularVentas;
